import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import { FORM_KEYS } from '../../../constants/formKeys';
import {
  formatMobileNumberInput,
  formatMobileNumberWithCountryCode,
  formatNumberWithoutSpaces
} from '../../../utils/formatNumber';
import { SLICES } from '../../../constants/slices';
import useVerify from '../../verification/useVerify';
import { useUpdateContactDetailsMutation, useGetContactDetailsQuery } from '../../../data/policy';
import { VALIDATION_ERROR_MESSAGE } from '../../../constants/errorMessage';
import { validateAustralianMobileNumber } from '../../../utils/validationUtils';
import { getAllPolicyHolders } from '../../../helpers/policyHelpers';
import { legacyPolicyNumberHandler } from '../../../helpers/paymentHelper';

const MOBILE_NUMBER_INCLUDE_SPACE_LENGTH = 12;

const useUpdatePhone = () => {
  const [updateContactDetails] = useUpdateContactDetailsMutation();
  const { watch, setValue, trigger } = useFormContext();
  const { tokenClaims, authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { petDetailsList } = useSelector((state) => state?.[SLICES.PET_DETAILS]);
  const policyHolders = getAllPolicyHolders({ petDetails: petDetailsList });
  const currentPolicyHolder = policyHolders.length >= 1 && policyHolders[0];
  const { data: contactDetails, isLoading: contactDetailsLoading } = useGetContactDetailsQuery({
    policyNumber: legacyPolicyNumberHandler(currentPolicyHolder?.policyNumber),
    auth0Token: authDetails?.auth0AccessToken
  });
  const [isUpdatePhoneStep, setIsUpdatePhoneStep] = React.useState(true);
  const navigate = useNavigate();

  const newMobileNumberValue = watch(FORM_KEYS.NEW_MOBILE_NUMBER);
  const ausNumberWithCountryCode = newMobileNumberValue && formatMobileNumberWithCountryCode(newMobileNumberValue);

  const { getVerifyCode, isCodeSent, isCodeVerified, confirmUserEnrollment, errorMessage, timeLeft } = useVerify({
    verifyType: 'phone',
    verifyTypeInput: ausNumberWithCountryCode,
    verifyCode: watch(FORM_KEYS.VERIFY_PHONE_CODE),
    skipRedirection: true,
    isEnabled: false
  });

  const steps = [
    { isCompleted: !isUpdatePhoneStep, isActive: isUpdatePhoneStep, label: 'UPDATE PHONE' },
    { isCompleted: false, isActive: !isUpdatePhoneStep, label: 'VERIFY PHONE' }
  ];

  useEffect(() => {
    const updateNewPhone = async () => {
      const updatePhonePayload = {
        policyNumber: contactDetails?.policyNumber,
        phoneMobile: formatNumberWithoutSpaces(newMobileNumberValue),
        phoneHome: '',
        phoneWork: '',
        email: contactDetails?.email
      };
      try {
        await updateContactDetails(updatePhonePayload);
      } catch (error) {
        throw error('Error updating new phone number: ', error);
      }
    };
    if (isCodeVerified) {
      updateNewPhone();
    }
  }, [
    isCodeVerified,
    ausNumberWithCountryCode,
    tokenClaims?.email,
    updateContactDetails,
    petDetailsList,
    newMobileNumberValue,
    contactDetails?.policyNumber,
    contactDetails?.email
  ]);

  useEffect(() => {
    if (!errorMessage && isCodeSent) {
      setIsUpdatePhoneStep(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage, isCodeSent]);

  const onUpdateClick = useCallback(() => {
    if (!isCodeSent) {
      getVerifyCode();
      if (errorMessage) {
        setIsUpdatePhoneStep(true);
      }
    }
  }, [errorMessage, getVerifyCode, isCodeSent]);

  const onCancelClick = useCallback(() => {
    navigate(CSPUrlsMap.accountDetails.url);
  }, [navigate]);

  const onInputChange = useCallback(() => {
    const currentValue = watch(FORM_KEYS.NEW_MOBILE_NUMBER);
    const formattedValue = formatMobileNumberInput(currentValue);
    setValue(FORM_KEYS.NEW_MOBILE_NUMBER, formattedValue);
    trigger(FORM_KEYS.NEW_MOBILE_NUMBER);
  }, [setValue, trigger, watch]);

  const validation = {
    minLength: {
      value: MOBILE_NUMBER_INCLUDE_SPACE_LENGTH,
      message: VALIDATION_ERROR_MESSAGE.INVALID_MOBILE_NUMBER
    },
    validate: {
      startsWith04: (value) => validateAustralianMobileNumber(value) || VALIDATION_ERROR_MESSAGE.INVALID_MOBILE_NUMBER
    }
  };

  return {
    steps,
    isUpdatePhoneStep,
    onUpdateClick,
    onCancelClick,
    onInputChange,
    validation,
    ausNumberWithCountryCode,
    getVerifyCode,
    isCodeSent,
    isCodeVerified,
    confirmUserEnrollment,
    errorMessage,
    timeLeft,
    navigate,
    contactDetailsLoading
  };
};

export default useUpdatePhone;
